import { V2CollectionItemSettings } from "@pages/CollectionsPage/V2CollectionItemSettings/V2CollectionItemSettings";
import { Tag } from "data-hooks/company/tags/tags.type";
import { query, where } from "firebase/firestore";
import { t } from "i18next";
import { DataTableData, DataTableHeader } from "../../../../../components/deprecated/DataTable/DataTable";
import { Popup } from "../../../../../components/deprecated/Popup/Popup";
import { PopupTemplates } from "../../../../../components/deprecated/PopupTemplates/PopupTemplates";
import { ReadAccessPopup } from "../../../../../components/deprecated/ReadAccessPopup/ReadAccessPopup";
import { ToastUtilities } from "../../../../../libs/react-toastify/ToastUtilities";
import { SystemsAndComponentsUtilities } from "../../../../../utils/systemsAndComponentsUtilities";
import { Utilities } from "../../../../../utils/utilities";
import { CollectionData } from "../../../collections/CollectionData";
import { CollectionFieldData } from "../../../collections/CollectionFieldData";
import { CustomFieldDataType } from "../../../Company/Collections/CustomFieldDataType";
import { CustomFieldDateDataType } from "../../../Company/Collections/CustomFieldDataTypeHelper/CustomFieldDateDataType";
import { DataObjectTagField } from "../../DataObjectField/CustomFields/DeviationDataFields";
import { DataObjectCollectionItemField } from "../../DataObjectField/CustomFields/FirebaseDataCollectionItemFields";
import { FirebaseDataContractorField } from "../../DataObjectField/CustomFields/FirebaseDataContractorField";
import { FirebaseDataGlobalDataTypeFields } from "../../DataObjectField/CustomFields/FirebaseDataCustomDataTypeFields";
import { FirebaseDataNorwegianStandardField } from "../../DataObjectField/CustomFields/FirebaseDataNorwegianStandardField";
import { FirebaseDataPropertyField } from "../../DataObjectField/CustomFields/FirebaseDataPropertyField";
import { FirebaseDataUnitField } from "../../DataObjectField/CustomFields/FirebaseDataUnitField";
import { DataObjectField } from "../../DataObjectField/DataObjectField";
import { FirebaseDataDateField } from "../../DataObjectField/Generic/FirebaseDataDateField";
import { FirebaseDataNumberField } from "../../DataObjectField/Generic/FirebaseDataNumberField";
import { FirebaseDataStringField } from "../../DataObjectField/Generic/FirebaseDataStringField";
import { DataObjectAction, DataObjectHelper } from "../../DataObjects/DataObjectHelper";
import { LocalData } from "../../LocalData";
import { V2CollectionItemData } from "../../V2CollectionItemData";

export class V2CollectionItemDataHelper extends DataObjectHelper<V2CollectionItemData> {
  id = () => `collectionItems-${this.collection.id}`;

  override displayName = () => this.collection.name.translate(1) ?? t("main.collection");

  override displayNamePlural = () => this.collection.name.translate(2) ?? t("main.collections");

  collection: CollectionData;

  constructor(collection: CollectionData) {
    super();
    this.collection = collection;
  }

  nameField = new FirebaseDataStringField<V2CollectionItemData>("name", t("main.name"), (item) => item.name, "title");

  propertiesField = new FirebaseDataPropertyField<V2CollectionItemData>("property", t("main.property"), (item) => item.properties, "business", true);

  protected getFieldsForCollection(): DataObjectField<any, any>[] {
    const fields: DataObjectField<any, any>[] = [];
    fields.push(this.nameField);
    fields.push(this.propertiesField);
    for (const field of this.getFields()) {
      fields.push(getDataObjectField(field, this.collection));
    }

    return fields;
  }

  allFields() {
    return this.getFieldsForCollection();
  }

  override getAllDataTableDatas(documents: V2CollectionItemData[]): DataTableData[] {
    const dataObjectFields: DataObjectField<any, any>[] = [];
    for (const field of this.getFields()) {
      dataObjectFields.push(getDataObjectField(field, this.collection));
    }

    return documents.map((item) => {
      const data: DataTableData = {
        key: item.id,
        cells: {
          name: item.name,
          properties: item.properties.map((p) => p.name).join(", "),
          visibleFor: Utilities.getReadAccessString(item.readAccess, undefined),
        },
      };

      for (const field of dataObjectFields) {
        const itemData = field.dataGetter(item);
        if (field.fullIsValueValid(itemData)) {
          data.cells[field.key] = field.getReadableStringRepresentation(itemData);
        } else {
          data.cells[field.key] = undefined;
        }
      }

      return data;
    });
  }

  override getAllDataTableHeaders(): DataTableHeader[] {
    const headers: DataTableHeader[] = [
      {
        key: "name",
        title: t("main.name"),
      },
      {
        key: "properties",
        title: t("main.properties"),
      },
      {
        key: "visibleFor",
        title: t("main.visibleFor"),
      },
    ];
    for (const fields of this.getFields()) {
      headers.push({
        key: fields.fieldID,
        title: fields.name.translate(),
      });
    }
    return headers;
  }

  getFields = () => LocalData.collectionFields.documents.filter((field) => field.collection?.id === this.collection.id);

  loadAll(): Promise<any> {
    const promises: Promise<any>[] = [];
    promises.push(this.collection.load());
    promises.push(
      new Promise((resolve) =>
        LocalData.collectionFields.loadWithFilter((ref) => query(ref, where("collectionID", "==", this.collection.id)), resolve)
      )
    );

    return Promise.all(promises);
  }

  getAllDatas(): V2CollectionItemData[] {
    return LocalData.collectionItems.documents.filter((item) => item.collection === this.collection);
  }

  override getActionsForData = (data: V2CollectionItemData): DataObjectAction<V2CollectionItemData>[] => {
    const actions: DataObjectAction<V2CollectionItemData>[] = [];
    const collection = data.collection;

    if (data.hasWriteAccess()) {
      actions.push({
        id: "changeAccess",
        iconName: "visibility",
        title: t("main.changeVisibility"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            Popup.present((id) => (
              <ReadAccessPopup
                id={id}
                readAccess={dataObjects[0].readAccess}
                requiredAccess={collection?.requiredWriteAccess}
                onSelect={(access) => {
                  resolve({ dataObjects, data: access });
                }}
                context={{ propertyID: undefined }}
                accessGroups={["roles", "contractorGroups"]}
              />
            ));
          });
        },
        performAction: (updateData) => {
          return ToastUtilities.presentPromise(t("main.savingData"), data.saveUpdate({ readAccess: Utilities.convertReadAccess(updateData) }));
        },
      });
      actions.push({
        id: "settings",
        iconName: "edit",
        title: t("main.edit"),
        maxSelected: 1,
        performAction: () => {
          Popup.present((id) => <V2CollectionItemSettings id={id} collection={this.collection} item={data} didSave={() => {}} />);
          return Promise.resolve();
        },
      });
      actions.push({
        id: "delete",
        iconName: "delete",
        title: t("main.delete"),
        requiredConsent: (dataObjects) => {
          return new Promise((resolve) => {
            PopupTemplates.presentVerifyDeleteDatas(
              this,
              dataObjects.map((data) => data.name),
              () => {
                resolve({ dataObjects });
              },
              t("main.cannotBeUndone")
            );
          });
        },
        performAction: () => {
          return ToastUtilities.presentPromise(t("main.deleting"), LocalData.collectionItems.deleteDocumentWithDeletedBy(data.id));
        },
      });
    }

    return actions;
  };

  getLinkTo(key: string): string | undefined {
    return `/collectionItems/${key}`;
  }
}

const getDataObjectField = (field: CollectionFieldData, collection: CollectionData): DataObjectField<any, any> => {
  switch (field.dataType) {
    case CustomFieldDataType.TEXT: {
      return new FirebaseDataStringField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "edit"
      );
    }
    case CustomFieldDataType.NUMBER: {
      return new FirebaseDataNumberField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "pin"
      );
    }
    case CustomFieldDataType.DATE: {
      return new FirebaseDataDateField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => {
          const value = item.fields[field.fieldID]?.value;
          return CustomFieldDateDataType.getDateFieldAsDate(value, field.dataOptions);
        },
        "event"
      );
    }
    case CustomFieldDataType.TAGS: {
      return new DataObjectTagField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => {
          const value = item.fields[field.fieldID]?.value;
          if (typeof value === "string") {
            return LocalData.getTagWithId(value);
          } else if (Array.isArray(value)) {
            const tags: Tag[] = [];
            for (const tagID of value) {
              const tag = LocalData.getTagWithId(tagID);
              if (tag) tags.push(tag);
            }
            return tags;
          }
          return undefined;
        },
        "label",
        true
      );
    }
    case CustomFieldDataType.CONTRACTORS: {
      return new FirebaseDataContractorField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "construction",
        true
      );
    }
    case CustomFieldDataType.NORWEGIAN_STANDARDS: {
      return new FirebaseDataNorwegianStandardField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => {
          const value = item.fields[field.fieldID]?.value;
          if (typeof value === "string") {
            const option = SystemsAndComponentsUtilities.getOptionWithKey(undefined, value);
            return option ? { id: option.id, name: option.title } : undefined;
          }
          return undefined;
        },
        "flag"
      );
    }
    case CustomFieldDataType.COLLECTION_ITEM: {
      return new DataObjectCollectionItemField<V2CollectionItemData>(
        collection,
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "flag"
      );
    }
    case CustomFieldDataType.CUSTOM_DATA_TYPE: {
      if (field.dataOptions.customDataTypeID) {
        return new FirebaseDataGlobalDataTypeFields<V2CollectionItemData>(
          LocalData.dataTypes.addData(field.dataOptions.customDataTypeID),
          field.fieldID,
          field.name.translate(),
          (item) => item.fields[field.fieldID]?.value,
          "flag"
        );
      }
      return new FirebaseDataStringField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "edit"
      );
    }

    case CustomFieldDataType.TENANTS: {
      return new FirebaseDataUnitField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "business"
      );
    }
    default: {
      return new FirebaseDataStringField<V2CollectionItemData>(
        field.fieldID,
        field.name.translate(),
        (item) => item.fields[field.fieldID]?.value,
        "edit"
      );
    }
  }
};
